import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import analyticsService from '../services/analyticsService';
import { useTranslate } from '../lib/i18n';
import { Button } from './ui/button';
import MarkdownRenderer from './MarkdownRenderer';

interface Props {
  showInformationModal: boolean;
  onClose: () => void;
}

const InformationModal = ({ showInformationModal, onClose }: Props) => {
  const [isAdditionalInfoOpen, setIsAdditionalInfoOpen] = useState(false);
  const t = useTranslate();

  const handleClose = () => {
    analyticsService.trackEvent({
      eventName: 'cookieNotificationClosed',
    });
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const toggleAdditionalInfo = () => {
    if (!isAdditionalInfoOpen) {
      analyticsService.trackEvent({
        eventName: 'openCookieNotificationHyvaTieta',
      });
    }
    setIsAdditionalInfoOpen(!isAdditionalInfoOpen);
  };

  useEffect(() => {
    if (showInformationModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showInformationModal]);

  if (!showInformationModal) return null;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={handleOverlayClick}
        className="fixed left-0 top-0 z-99999 h-screen w-full bg-black/50 overflow-y-auto py-8"
      >
        <div
          className="relative mx-auto my-auto w-[min(calc(100%-3rem),32rem)] bg-popover text-popover-foreground shadow-lg rounded-lg"
          style={{
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            margin: '4vh auto',
          }}
        >
          <div className="p-4 sm:p-5 md:p-6">
            <div className="flex items-start justify-between">
              <h2 className="font-semibold" style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}>
                {t('cookieNotification.title')}
              </h2>
            </div>

            <div className="mt-4 space-y-3">
              <MarkdownRenderer markdown={t('cookieNotification.markdownDescription')} />

              <div className="border-t"></div>

              <div>
                <div
                  className="flex cursor-pointer items-center justify-between"
                  onClick={toggleAdditionalInfo}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      toggleAdditionalInfo();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-center">
                    <Info size={18} className="mr-2" />
                    <h3>{t('cookieNotification.goodToKnow')}</h3>
                  </div>
                  {isAdditionalInfoOpen ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </div>
                <div
                  className="grid transition-all duration-300 ease-in-out overflow-hidden"
                  style={{
                    gridTemplateRows: isAdditionalInfoOpen ? '1fr' : '0fr',
                  }}
                >
                  <div className="min-h-0 pl-4">
                    <div className="h-4" />
                    <MarkdownRenderer
                      markdown={t('cookieNotification.secondMarkdownDescription')}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t"></div>

              <div>
                <h3
                  className="mb-2 font-semibold"
                  style={{ fontSize: 'clamp(0.875rem, 1.75vw, 1rem)' }}
                >
                  {t('cookieNotification.importantNoteTitle')}
                </h3>
                <p>{t('cookieNotification.importantNoteBody')}</p>
              </div>

              <div className="flex space-x-4">
                <Button onClick={handleClose} className="w-full">
                  {t('common.buttons.close')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InformationModal;
